import { useEffect, useState } from "react";
import {
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { ProductDetailData } from "./ProductDetailTypes";
import ProductImageGallery from "./ProductImageGallery/ProductImageGallery";
import ProductDetailCenter from "./ProductDetailCenter/ProductDetailCenter";
import ProductPurchaseCard from "./ProductPurchaseCard/ProductPurchaseCard";
import SimilarProductsComparison from "./SimilarProductsComparison/SimilarProductsComparison";
import ProductReviews from "./ProductReviews/ProductReviews";
import RelatedItemsSection from "../ProductRecommendations/RelatedItemsSection/RelatedItemsSection";
import CustomersAlsoBoughtSection from "../ProductRecommendations/CustomersAlsoBoughtSection/CustomersAlsoBoughtSection";
import RelevantItemsSection from "../ProductRecommendations/RelevantItemsSection/RelevantItemsSection";
import CategoryBestSellersSection from "../ProductRecommendations/CategoryBestSellersSection/CategoryBestSellersSection";
import "./ProductDetailPage.css";
import { useCart } from "../../../context/CartContext";
import { useWishlist } from "../../../context/WishlistContext";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import SideCart from "../../globalcomponents/SideCart/SideCart";
import ProductBreadcrumbs from "../../globalcomponents/ProductBreadcrumbs/ProductBreadcrumbs";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import MobileProductStickyActions from "../../globalcomponents/mobile/MobileProductStickyActions/MobileProductStickyActions";
import { useAppDispatch } from "../../../Redux/hooks";
import {
    getProductReviewPercentage,
    getProductOverallRating,
    getReviewsByProductId,
} from "../../../Redux/products review/slice";
import { recordActivity } from "../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../Redux/ActivityApi/types";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

type LocationState = {
    product?: ProductDetailData;
};

const mapCatalogToProductDetail = (data: any): ProductDetailData => {
    const firstVariant = data.variants?.[0];
    const featuredOffer = firstVariant?.featuredOffer || data.featuredOffer;
    const price = Number(
        featuredOffer?.finalPrice ??
        firstVariant?.pricing?.finalPrice ??
        featuredOffer?.unitPrice ??
        firstVariant?.unitPrice ??
        0
    );
    const mrp = Number(
        featuredOffer?.mrp ??
        firstVariant?.pricing?.mrp ??
        data.mrp ??
        price
    );
    const superCat = data.categoryHierarchy?.superCategory || "-";
    const cat = data.categoryHierarchy?.category;
    const subCat = data.categoryHierarchy?.subCategory;
    const breadcrumb = [
        "Home",
        superCat !== "-" ? superCat : null,
        cat,
        subCat,
        data.name,
    ].filter(Boolean) as string[];

    const galleryOnlyImages = Array.from(
        new Set(
            [
                ...(Array.isArray(data.images) ? data.images : []),
                ...(data.variants?.flatMap((v: any) => [
                    ...(Array.isArray(v.images) ? v.images : []),
                ]) || []),
            ].filter((img): img is string => Boolean(img) && typeof img === "string" && img.trim() !== "" && img !== data.mainImage)
        )
    );

    const allImages = galleryOnlyImages;

    const colors = (data.variants || [])
        .map((v: any) => {
            const name =
                v.variationAttributes?.Color ||
                v.variationAttributes?.color ||
                v.variationAttributes?.Colour ||
                v.attributes?.Color ||
                v.attributes?.color;
            if (!name) return null;
            return {
                name,
                image: v.mainImage || data.mainImage,
            };
        })
        .filter(
            (c: any, idx: number, arr: any[]) =>
                c && arr.findIndex((x) => x.name.toLowerCase() === c.name.toLowerCase()) === idx
        );

    const sizes = Array.from(
        new Set(
            (
                data.variationMatrix?.Size ||
                data.variationMatrix?.size ||
                data.variants?.map(
                    (v: any) =>
                        v.variationAttributes?.Size ||
                        v.variationAttributes?.size ||
                        v.attributes?.Size
                ) ||
                []
            ).filter(Boolean)
        )
    ) as string[];

    const variants = (data.variants || []).map((v: any) => {
        const vOffer = v.featuredOffer || {};
        const vPrice = Number(vOffer.finalPrice ?? v.pricing?.finalPrice ?? v.unitPrice ?? price);
        const vMrp = Number(vOffer.mrp ?? v.pricing?.mrp ?? vPrice);
        const label =
            Object.values(v.variationAttributes || {}).join(" / ") ||
            v.sku ||
            v.ssin ||
            "-";
        return {
            _id: v._id || v.id,
            id: v._id || v.id,
            label,
            price: vPrice,
            mrp: vMrp,
            offers: v.offers || [],
            featuredOffer: v.featuredOffer || null,
        };
    });

    const featuredSellerSkuId = featuredOffer?.sellerSkuId;
    const featuredSellerId = featuredOffer?.sellerId;

    const rawOtherOffers = (firstVariant?.offers || []).filter(
        (o: any) =>
            o &&
            o.sellerSkuId !== featuredSellerSkuId &&
            (o.sellerId !== featuredSellerId || !featuredSellerId)
    );

    const rawBrand = typeof data.brand === "string" ? data.brand : data.brand?.name || data.brandName;
    const brandName = typeof rawBrand === "string" && rawBrand.trim() ? rawBrand.trim() : "-";

    const rawSeller =
        (typeof featuredOffer?.sellerName === "string" && featuredOffer.sellerName.trim() ? featuredOffer.sellerName.trim() : undefined) ||
        (typeof data.sellerName === "string" && data.sellerName.trim() ? data.sellerName.trim() : undefined) ||
        (typeof data.vendors?.marketedBy === "string" && data.vendors.marketedBy.trim() ? data.vendors.marketedBy.trim() : undefined) ||
        (brandName !== "-" ? brandName : undefined);
    const sellerName = rawSeller || "-";

    const isFreeDelivery = Boolean(
        featuredOffer?.freeDelivery ??
        featuredOffer?.delivery?.freeDelivery ??
        data.featuredOffer?.freeDelivery ??
        firstVariant?.offers?.[0]?.delivery?.freeDelivery
    );

    const isFastDelivery = Boolean(
        featuredOffer?.fastDelivery ??
        featuredOffer?.delivery?.fastDelivery ??
        data.featuredOffer?.fastDelivery ??
        firstVariant?.offers?.[0]?.delivery?.fastDelivery
    );

    const isNextDayDelivery = Boolean(
        featuredOffer?.nextDayDelivery ??
        featuredOffer?.delivery?.nextDayDelivery ??
        data.featuredOffer?.nextDayDelivery
    );

    const sellerId =
        featuredOffer?.sellerId ||
        data.featuredOffer?.sellerId ||
        data.creatorId ||
        "";

    const rawShipsFrom =
        data.shipsFrom ||
        data.fulfillmentBy ||
        featuredOffer?.shipsFrom ||
        featuredOffer?.fulfillmentBy ||
        data.delivery?.shipsFrom ||
        data.attributes?.["Ships from"] ||
        data.attributes?.shipsFrom ||
        (isFreeDelivery || isFastDelivery ? "Salar Fulfilment" : undefined) ||
        data.vendors?.marketedBy ||
        (data.country ? data.country : undefined);
    const shipsFrom = typeof rawShipsFrom === "string" && rawShipsFrom.trim() ? rawShipsFrom.trim() : "-";

    const rawPackaging =
        data.packaging ||
        data.attributes?.Packaging ||
        data.attributes?.packaging ||
        data.attributes?.["Product Packaging"] ||
        data.packagingType ||
        (data.condition === "new" ? "Protected product packaging" : data.condition ? `${data.condition} packaging` : undefined);
    const packaging = typeof rawPackaging === "string" && rawPackaging.trim() ? rawPackaging.trim() : "-";

    const rawPayment =
        data.payment ||
        data.paymentMethods ||
        data.paymentSecurity ||
        data.attributes?.Payment ||
        data.attributes?.payment ||
        (featuredOffer || price > 0 ? "Secure transaction" : undefined);
    const payment = typeof rawPayment === "string" && rawPayment.trim() ? rawPayment.trim() : "-";

    const rawGiftOptions =
        typeof data.giftOptions === "string"
            ? data.giftOptions
            : data.giftOptions === true || data.giftWrap === true || data.attributes?.GiftOptions === "Available"
            ? "Available at checkout"
            : data.giftOptions === false || data.giftWrap === false
            ? "Not available"
            : data.attributes?.["Gift options"] || data.attributes?.GiftOptions;
    const giftOptions = typeof rawGiftOptions === "string" && rawGiftOptions.trim() ? rawGiftOptions.trim() : "-";

    const country = typeof data.country === "string" && data.country.trim() ? data.country.trim() : "-";

    const faqWarranty = data.faqs?.find(
        (f: any) =>
            typeof f.question === "string" &&
            (f.question.toLowerCase().includes("warranty") || f.question.toLowerCase().includes("guarantee"))
    )?.answer;
    const warranty =
        (typeof data.warranty === "string" && data.warranty.trim() ? data.warranty.trim() : undefined) ||
        (typeof data.attributes?.Warranty === "string" && data.attributes.Warranty.trim() ? data.attributes.Warranty.trim() : undefined) ||
        (typeof faqWarranty === "string" && faqWarranty.trim() ? faqWarranty.trim() : undefined);

    const faqReturn = data.faqs?.find(
        (f: any) =>
            typeof f.question === "string" &&
            (f.question.toLowerCase().includes("return") || f.question.toLowerCase().includes("replacement"))
    )?.answer;
    const returnPolicy =
        (typeof data.returnPolicy === "string" && data.returnPolicy.trim() ? data.returnPolicy.trim() : undefined) ||
        (typeof data.attributes?.["Return Policy"] === "string" && data.attributes["Return Policy"].trim() ? data.attributes["Return Policy"].trim() : undefined) ||
        (typeof faqReturn === "string" && faqReturn.trim() ? faqReturn.trim() : undefined);

    return {
        id: data._id || data.ssin,
        title: data.name || "-",
        description: data.description || (data.bulletPoints?.length ? data.bulletPoints.join(". ") : "-"),
        mainImage: data.mainImage || "",
        image: allImages[0] || data.mainImage || "",
        images: allImages,
        price,
        mrp,
        rating: data.reviews?.averageRating,
        reviews: data.reviews?.totalReviews ? String(data.reviews.totalReviews) : undefined,
        deal: featuredOffer?.deal?.dealName,
        breadcrumb,
        category: superCat,
        brand: brandName,
        sellerName,
        sellerSlug: sellerName !== "-" ? sellerName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : undefined,
        sellerId,
        stockStatus: featuredOffer?.stockStatus || (featuredOffer?.availableQty === 0 ? "out_of_stock" : "in_stock"),
        availableQty: featuredOffer?.availableQty ?? 0,
        freeDelivery: isFreeDelivery,
        fastDelivery: isFastDelivery,
        nextDayDelivery: isNextDayDelivery,
        shipsFrom,
        packaging,
        payment,
        giftOptions,
        country,
        condition: data.condition,
        returnPolicy,
        warranty,
        faqs: data.faqs,
        vendors: data.vendors,
        sellerCount: data.sellerCount ?? (rawOtherOffers.length + 1),
        otherOffers: rawOtherOffers,
        colors: colors.length > 0 ? colors : undefined,
        sizes: sizes.length > 0 ? sizes : undefined,
        variants: variants.length > 0 ? variants : undefined,
        bulletPoints: data.bulletPoints || [],
        attributes: data.attributes || {},
        similarProducts: data.similarProducts || [],
        boughtTogetherProducts: data.boughtTogetherProducts || [],
        frequentlyBoughtProducts: data.frequentlyBoughtProducts || [],
    };
};

const ProductDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { addToCart, setCartOpen, cartCount } = useCart();
    const { toggleWishlist } = useWishlist();

    const stateProduct = (location.state as LocationState)?.product;
    const normalizedStateProduct: ProductDetailData | null = stateProduct ? {
        ...stateProduct,
        brand: typeof stateProduct.brand === "string" && stateProduct.brand.trim() ? stateProduct.brand.trim() : (typeof (stateProduct.brand as any)?.name === "string" ? (stateProduct.brand as any).name : "-"),
        sellerName: typeof stateProduct.sellerName === "string" && stateProduct.sellerName.trim() ? stateProduct.sellerName.trim() : (typeof (stateProduct.sellerName as any)?.name === "string" ? (stateProduct.sellerName as any).name : "-"),
    } : null;
    const [product, setProduct] = useState<ProductDetailData | null>(normalizedStateProduct);
    const [loading, setLoading] = useState<boolean>(!normalizedStateProduct && Boolean(id));

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto",
        });
    }, [location.pathname, id]);

    useEffect(() => {
        if (!id) return;

        let cancelled = false;
        if (!stateProduct) {
            setLoading(true);
        }

        axios
            .get(`${PRODUCT_BASE_URL}/e-commerce/catalog/products/${id}`)
            .then((res) => {
                if (cancelled) return;
                const data = res.data?.data;
                if (data) {
                    const mapped = mapCatalogToProductDetail(data);
                    setProduct(mapped);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch product detail:", err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        // Review fetches as in DetailPage
        dispatch(getProductReviewPercentage(id));
        dispatch(getProductOverallRating(id));
        dispatch(getReviewsByProductId({ productId: id, page: 1, limit: 10 }));

        return () => {
            cancelled = true;
        };
    }, [dispatch, id, stateProduct]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (product && id) {
            timer = setTimeout(() => {
                dispatch(
                    recordActivity({
                        activities: {
                            eventType: ActivityType.VIEW_PRODUCT,
                            productId: id as string,
                        },
                    }),
                );
            }, 5000);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [product, id, dispatch]);

    const handleAddToCart = async (quantity: number) => {
        if (!product) return;
        await addToCart({
            productId: String(product.id),
            productName: product.title,
            productImage: product.image,
            productType: "PHYSICAL",
            variantId: product.variants?.[0]?._id || product.variants?.[0]?.id,
            quantity: quantity,
            unitPrice: product.price,
            price: product.price,
            mrp: product.mrp,
            sellerId: product.sellerId,
            sellerName: product.sellerName !== "-" ? product.sellerName : undefined,
            isMLMProduct: false,
            metadata: {
                mrp: product.mrp,
                image: product.image,
                description: product.description,
            },
        });
        setCartOpen(true);
    };

    const handleBuyNow = async (quantity: number) => {
        if (!product) return;
        await addToCart({
            productId: String(product.id),
            productName: product.title,
            productImage: product.image,
            productType: "PHYSICAL",
            variantId: product.variants?.[0]?._id || product.variants?.[0]?.id,
            quantity: quantity,
            unitPrice: product.price,
            price: product.price,
            mrp: product.mrp,
            sellerId: product.sellerId,
            sellerName: product.sellerName !== "-" ? product.sellerName : undefined,
            isMLMProduct: false,
            metadata: {
                mrp: product.mrp,
                image: product.image,
                description: product.description,
            },
        });
        navigate("/Cart");
    };

    const handleWishlist = () => {
        if (!product) return;
        toggleWishlist(String(product.id), {
            productVariantId: product.variants?.[0]?._id || product.variants?.[0]?.id,
        });
    };

    if (loading) {
        return (
            <>
                <div className="product-detail-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} />
                <div
                    style={{
                        maxWidth: 1560,
                        margin: "40px auto",
                        padding: "0 24px",
                        display: "flex",
                        gap: 24,
                        minHeight: 500,
                    }}
                >
                    <div
                        style={{
                            flex: "0 0 45%",
                            height: 480,
                            borderRadius: 16,
                            background: "#f3f4f6",
                            animation: "pulse 1.5s infinite",
                        }}
                    />
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
                        <div style={{ width: "80%", height: 32, borderRadius: 8, background: "#f3f4f6" }} />
                        <div style={{ width: "50%", height: 24, borderRadius: 8, background: "#f3f4f6" }} />
                        <div style={{ width: "30%", height: 40, borderRadius: 8, background: "#f3f4f6", marginTop: 24 }} />
                        <div style={{ width: "100%", height: 180, borderRadius: 12, background: "#f3f4f6", marginTop: 16 }} />
                    </div>
                </div>
            </>
        );
    }

    if (!product) {
        return (
            <>
                <div className="product-detail-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} />
                <div className="product-detail-not-found">
                    <h2>Product not found</h2>
                    <p>The product information is currently unavailable.</p>
                    <button type="button" onClick={() => navigate("/HomePage")}>
                        <ArrowLeft size={16} />
                        Back to Home
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="product-detail-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <MobileDetailNavbar cartCount={cartCount} />

            <ProductBreadcrumbs items={product.breadcrumb} />

            <main className="product-detail-page">
                <div className="product-detail-container">
                    <div className="product-detail-left">
                        <ProductImageGallery
                            title={product.title}
                            image={product.mainImage || ""}
                            images={product.images}
                            description={product.description}
                            color={product.colors?.[0]?.name || "-"}
                            size={product.sizes?.[0] || "-"}
                        />
                    </div>

                    <div className="product-detail-center">
                        <ProductDetailCenter product={product} />

                        <div className="product-detail-mobile-purchase">
                            <ProductPurchaseCard
                                productId={product.id}
                                productTitle={product.title}
                                productImage={product.image}
                                productDescription={product.description}
                                price={product.price}
                                mrp={product.mrp}
                                variantId={product.variants?.[0]?._id || product.variants?.[0]?.id}
                                sellerName={product.sellerName || "-"}
                                sellerSlug={product.sellerSlug}
                                sellerId={product.sellerId}
                                stock={product.stockStatus !== "out_of_stock"}
                                freeDelivery={product.freeDelivery}
                                fastDelivery={product.fastDelivery}
                                nextDayDelivery={product.nextDayDelivery}
                                shipsFrom={product.shipsFrom}
                                packaging={product.packaging}
                                payment={product.payment}
                                giftOptions={product.giftOptions}
                                category={product.category}
                                returnPolicy={product.returnPolicy}
                                warranty={product.warranty}
                                otherOffers={product.otherOffers}
                                sellerCount={product.sellerCount}
                                onAddToCart={handleAddToCart}
                                onBuyNow={handleBuyNow}
                                onWishlist={handleWishlist}
                            />
                        </div>
                    </div>

                    <aside className="product-detail-right">
                        <ProductPurchaseCard
                            productId={product.id}
                            productTitle={product.title}
                            productImage={product.image}
                            productDescription={product.description}
                            price={product.price}
                            mrp={product.mrp}
                            variantId={product.variants?.[0]?._id || product.variants?.[0]?.id}
                            sellerName={product.sellerName || "-"}
                            sellerSlug={product.sellerSlug}
                            sellerId={product.sellerId}
                            stock={product.stockStatus !== "out_of_stock"}
                            freeDelivery={product.freeDelivery}
                            fastDelivery={product.fastDelivery}
                            nextDayDelivery={product.nextDayDelivery}
                            shipsFrom={product.shipsFrom}
                            packaging={product.packaging}
                            payment={product.payment}
                            giftOptions={product.giftOptions}
                            category={product.category}
                            returnPolicy={product.returnPolicy}
                            warranty={product.warranty}
                            otherOffers={product.otherOffers}
                            sellerCount={product.sellerCount}
                            onAddToCart={handleAddToCart}
                            onBuyNow={handleBuyNow}
                            onWishlist={handleWishlist}
                        />
                    </aside>
                </div>

                <SimilarProductsComparison product={product} />

                <RelatedItemsSection
                    category={product.category}
                    products={product.similarProducts}
                />

                <CustomersAlsoBoughtSection
                    category={product.category}
                    products={
                        product.boughtTogetherProducts?.length
                            ? product.boughtTogetherProducts
                            : product.frequentlyBoughtProducts
                    }
                />

                <ProductReviews product={product} />

                <RelevantItemsSection category={product.category} />

                <CategoryBestSellersSection category={product.category} />
            </main>

            <MobileProductStickyActions
                onAddToCart={() => handleAddToCart(1)}
                onBuyNow={() => handleBuyNow(1)}
            />

            <SideCart />
        </>
    );
};

export default ProductDetailPage;